import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type FunctionComponent,
  type PropsWithChildren,
} from 'react';

export interface AccordionProps {
  /**
   * To style the Accordion with different colors
   */
  type?: 'flat' | 'outlined' | 'dividers';
  /**
   * To override the style for button
   */
  style?: CSSProperties;
  /**
   * To override the style for button
   */
  className?: string;
  /**
   * Define the default active (open) accordion item. This works only at
   * instantiation
   */
  defaultActive?: string | number;
  /**
   * Allow you to manualy manage items opening. This functionnality works in
   * pair with `isOpen` and `onToggle` props from AccordionItem. Else this
   * component not works
   */
  manual?: boolean;
  /**
   * Allows to display or not the arrow
   */
  hiddenArrowIcon?: boolean;
}

interface AccordionContext {
  type: AccordionProps['type'];
  manual: boolean;
  hiddenArrowIcon: boolean;
  activeAccordion?: string | number;
  onToggleAccordion?(selectedAccordion: string | number): void;
}

const AccordionContext = createContext<AccordionContext>({
  type: 'flat',
  manual: false,
  hiddenArrowIcon: false,
});

/**
 * ## Usage
 *
 * The accordion component lets users show and hide sections of related content on a page.
 *
 * When to use :
 *
 * - To organize related information
 * - to shorten pages and reduce scrolling when content is not crucial to read in full.
 * - When space is at a premium and long content cannot be displayed all at once like on a mobile interface or in a side panel.
 */
export const Accordion: FunctionComponent<
  PropsWithChildren<AccordionProps>
> = ({
  className,
  defaultActive,
  style,
  type = 'flat',
  manual = false,
  hiddenArrowIcon = false,
  children,
}) => {
  const [activeAccordion, setActiveAccordion] = useState<
    string | number | undefined
  >(defaultActive);
  const onToggleAccordion = useCallback(
    (selectedAccordion: string | number) =>
      setActiveAccordion((currentActiveAccordion) =>
        currentActiveAccordion === selectedAccordion ? '' : selectedAccordion,
      ),
    [],
  );

  const contextValue = useMemo(
    () => ({
      manual,
      hiddenArrowIcon,
      type,
      activeAccordion,
      onToggleAccordion,
    }),
    [activeAccordion, hiddenArrowIcon, manual, onToggleAccordion, type],
  );

  const renderChildren =
    style || className ? (
      <div style={style} className={className}>
        {children}
      </div>
    ) : (
      children
    );

  return (
    <AccordionContext.Provider value={contextValue}>
      {renderChildren}
    </AccordionContext.Provider>
  );
};

Accordion.displayName = 'Accordion';

export const useAccordionContext = () => useContext(AccordionContext);
